import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { apiService } from '../services/api';

const WaitlistView: React.FC = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    role: '',
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus(null);
    setSubmitting(true);
    try {
      const response = await apiService.joinWaitlist(form);
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to join waitlist');
      }
      setStatus({ type: 'success', message: response?.message || 'Successfully joined waitlist' });
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        organization: '',
        role: '',
      });
    } catch (err: any) {
      setStatus({
        type: 'error',
        message: err?.response?.data?.error || err.message || 'Failed to join waitlist',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-3xl">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 text-center p-10">
          <h1 className="text-3xl font-bold text-indigo-600 mb-4">Welcome to Ruby Oncology Insights</h1>
          <p className="text-slate-500 text-lg">
            Your go-to platform for oncology insights and NCCN guidelines.
          </p>
          <div className="mt-6">
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
            >
              Login
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-card border border-gray-100 mt-8 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 text-center">
            <h2 className="text-white text-2xl font-semibold">Join Our Waitlist</h2>
          </div>
          <div className="p-8">
            <p className="text-center text-slate-500 mb-6">
              Be the first to know about new features and exclusive access to advanced oncology insights.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="space-y-1 text-sm font-medium text-slate-600">
                  First Name
                  <input
                    value={form.firstName}
                    onChange={handleChange('firstName')}
                    required
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Enter your first name"
                  />
                </label>
                <label className="space-y-1 text-sm font-medium text-slate-600">
                  Last Name
                  <input
                    value={form.lastName}
                    onChange={handleChange('lastName')}
                    required
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Enter your last name"
                  />
                </label>
              </div>
              <label className="space-y-1 text-sm font-medium text-slate-600 block">
                Email Address
                <input
                  value={form.email}
                  onChange={handleChange('email')}
                  required
                  type="email"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter your email address"
                />
              </label>
              <label className="space-y-1 text-sm font-medium text-slate-600 block">
                Organization
                <input
                  value={form.organization}
                  onChange={handleChange('organization')}
                  required
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter your organization name"
                />
              </label>
              <label className="space-y-1 text-sm font-medium text-slate-600 block">
                Job Title
                <input
                  value={form.role}
                  onChange={handleChange('role')}
                  required
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter your job title"
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-indigo-600 text-white font-semibold py-3 hover:bg-indigo-700 disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
            </form>

            {status && (
              <div
                className={`mt-4 rounded-lg px-4 py-3 text-sm ${
                  status.type === 'success'
                    ? 'border border-emerald-100 bg-emerald-50 text-emerald-700'
                    : 'border border-rose-100 bg-rose-50 text-rose-700'
                }`}
              >
                {status.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistView;
